import jwt_decode from 'jwt-decode';

import { request } from './http';
import { getTenantSubdomainFromUrl } from './urls';
import { APP_ID, BASE_URL, DEFAULT_TOKEN_NAME } from '../constants';
import { AccountData } from '../models';
import { getStore } from '../store';

export async function getSSOToken(tenantId?: string) {
  const tenantSubdomain = tenantId ? undefined : getTenantSubdomainFromUrl();
  const callbackUrl = `${window.location.protocol}//${window.location.host}`;
  return await request({
    url: `${BASE_URL}/identity/applications/${APP_ID}/token`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { tenantId, tenantSubdomain, callbackUrl },
  });
}

export function setToken(tokenName: string, tokenValue: string) {
  if (tokenName && tokenValue) {
    sessionStorage.setItem(tokenName, tokenValue);
  }
}

export function getToken() {
  return sessionStorage.getItem(DEFAULT_TOKEN_NAME);
}

export function getUserDataFromToken(jwt: any): AccountData {
  const tokenData = decodeJwt(jwt);

  if (tokenData && tokenData.account) {
    const {
      account: {
        tenantId,
        id,
        clients,
        surname,
        givenName,
        evoSbUserId,
        username,
        email,
      },
      scope,
      policy,
    } = tokenData;
    return {
      clients,
      email,
      evoSbUserId,
      givenName,
      id,
      scope,
      surname,
      tenantId,
      username,
      loadedFromToken: true,
      loadedFromDb: false,
      policy,
    };
  }

  // if token is bad, we'll find out soon enough;
  // it's not helpful to throw from here
  // TODO: its less helpful to return a bad AccountData object ;)
  //  and there are already paths that might throw (`decodeJwt(jwt)`)
  return {} as AccountData;
}

export async function getAppropriateToken(tenantId: string, audience: string): Promise<string> {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  const decodedToken = decodeJwt(token);
  const iss = decodedToken ? decodedToken.iss : null;

  // AsureId (Cognito) tokens are already usable directly; no exchange needed
  if (iss && iss.includes('cognito-idp')) {
    return token;
  }

  try {
    const tokenResponse = await request({
      url: `${BASE_URL}/identity/tenants/${tenantId}/oauth/token`,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: { grant_type: 'token_exchange', audience },
    });
    return tokenResponse.access_token;
  } catch (error) {
    console.error('Failed to exchange token:', error);
    throw new Error('Unable to retrieve token. Please try again later.');
  }
}

export function getApplicationIdFromToken(jwt: any): string | undefined {
  const tokenData = decodeJwt(jwt);
  return tokenData ? tokenData.applicationId : undefined;
}

export function getTenantSubdomainFromToken(jwt: any): string | undefined {
  const tokenData = decodeJwt(jwt);
  return tokenData ? tokenData.tenantSubdomain : undefined;
}

export function isAsureIdToken(token: string | null): boolean {
  if (!token) {
    return false;
  }
  const decodedToken = decodeJwt(token);
  return !!decodedToken?.iss?.includes('cognito-idp');
}

function getAsureAppOrigin(): string | undefined {
  const selectedTenantSubdomain = getStore()?.getState().tenants.selectedTenant?.subdomain;
  if (!selectedTenantSubdomain) {
    return undefined;
  }

  const { protocol, host } = window.location;
  const domainWithoutSubdomain = host.split('.').slice(1).join('.');
  return `${protocol}//${selectedTenantSubdomain}.${domainWithoutSubdomain}`;
}

export function getAsureAppOriginHeaders(): { [name: string]: string } {
  const token = getToken();
  if (!isAsureIdToken(token)) {
    return {};
  }

  const origin = getAsureAppOrigin();
  return origin ? { 'x-asure-app-origin': origin } : {};
}

function decodeJwt(jwt: string): any {
  try {
    return jwt_decode(jwt);
  } catch (e) {
    return undefined;
  }
}
